import React from 'react';
import { ListGroup } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag } from '@fortawesome/pro-solid-svg-icons';

import ContactMethod from './contact_method';
import ActionsDropdown from './actions_dropdown';
import ContactSection from './contact_section';
import { CONTACT_MODEL } from '../../../constants';
import ConnectTagManager from '../../tags/connect_tag_manager';
import ButtonGroupRadio from '../activity_forms/button_group_radio';
import { setVerifiedOwner } from 'bundles/connect/components/store/actions/owners';
import { connect } from 'react-redux';
import RethinkMode from '../../rethink_mode/rethink_mode';
import type { ContactAddress, ContactEmail, ContactPhoneNumber, Ownerships } from 'bundles/connect/types';

const VERIFIED_OPTIONS = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' }
];

type Props = {
  owner: Ownerships,
  renderLogActivity?: (contactMethodKey: string, contactMethod: ContactAddress | ContactEmail | ContactPhoneNumber) => React.ReactNode,
  setVerifiedOwner: (ownerId: number, verified: boolean) => void,
  showOwnerName?: boolean
}

function Ownership({ owner, renderLogActivity, setVerifiedOwner, showOwnerName }: Props) {
  const { contact } = owner;

  return (
    <ListGroup.Item className="pb-4">
      <div className="d-flex flex-column gap-3">
        {showOwnerName && (
          <div>
            <small className="text-muted fw-normal">{owner.contact.jobTitle || owner.label || 'Contact'}</small>
            <h6 className="text-body privacy-mask mb-0">{owner.contact.name}</h6>
          </div>
        )}
        <RethinkMode>
          <div className="d-flex align-items-center justify-content-between">
            <ConnectTagManager
              buttonProperties={{
                iconLeft: <FontAwesomeIcon icon={faTag} />,
              }}
              initialTags={owner.contact.tags}
              recordId={owner.contact.id}
              recordType={CONTACT_MODEL}
            />
            <ActionsDropdown owner={owner} />
          </div>
          {owner.id && (
            <div className="d-flex align-items-center justify-content-between">
              <div className="fw-bolder">
                Is this a verified owner?
              </div>
              <ButtonGroupRadio
                checked={owner.verified}
                name={`verified_owner_${owner.id}`}
                options={VERIFIED_OPTIONS}
                onChange={verified => setVerifiedOwner(owner.id, verified)}
              />
            </div>
          )}
        </RethinkMode>
        <div className="d-flex flex-column gap-3">
          {contact.phoneNumbers.length > 0 && (
            <ContactSection defaultOpen={true} title="Phone Numbers">
              <ContactMethod
                contactMethodKey="phoneNumbers"
                owner={owner}
                renderDisplayName={(phoneNumber: ContactPhoneNumber, textStyle) => (
                  <div className={`d-flex align-items-center flex-wrap ${textStyle}`}>
                    <a className="privacy-mask" href={`tel:${phoneNumber.number}`} target="_top">
                      {phoneNumber.formatted}
                    </a>
                    <small className="ms-1 text-muted fw-normal">{phoneNumber.label}</small>
                  </div>
                )}
                renderLogActivity={renderLogActivity}
              />
            </ContactSection>
          )}
          {contact.emails.length > 0 && (
            <ContactSection defaultOpen={true} title="Email Addresses">
              <ContactMethod
                contactMethodKey="emails"
                owner={owner}
                renderDisplayName={(email: ContactEmail, textStyle) => (
                  <a
                    className={`text-truncate privacy-mask ${textStyle}`}
                    href={`mailto:${email.email}`}
                    target="_top"
                  >
                    {email.email}
                  </a>
                )}
                renderLogActivity={renderLogActivity}
              />
            </ContactSection>
          )}
          {contact.addresses.length > 0 && (
            <ContactSection title="Addresses">
              <ContactMethod
                contactMethodKey="addresses"
                owner={owner}
                renderDisplayName={(address: ContactAddress, textStyle) => (
                  <div className={`${textStyle} lh-sm privacy-mask`}>
                    <div>{address.twoLine[0]}</div>
                    <div>{address.twoLine[1]}</div>
                  </div>
                )}
              />
            </ContactSection>
          )}
        </div>
      </div>
    </ListGroup.Item >
  );
}

export default connect(null, { setVerifiedOwner })(Ownership);
